/**
 * General event utilities for calendar operations.
 */

import { isBefore, parseISO, startOfDay, isSameDay, endOfDay, format } from 'date-fns';

/**
 * Determines if an event is in the past.
 *
 * For all-day events: past if the end date is before today
 * For timed events: past if the end time is before now
 */
export function isEventPast(event: { endTime: string; allDay?: boolean }): boolean {
  const now = new Date();
  const endTime = parseISO(event.endTime);

  if (event.allDay) {
    // All-day events are past if they ended before today
    return isBefore(endTime, startOfDay(now));
  }

  // Timed events are past if they've already ended
  return isBefore(endTime, now);
}

/**
 * Determines if a date is in the past (before today).
 */
export function isDatePast(date: Date): boolean {
  const today = startOfDay(new Date());
  return isBefore(startOfDay(date), today);
}

/**
 * Check if event spans multiple calendar days.
 *
 * An event is multi-day if its start and end times are on different calendar days.
 *
 * @example
 * // Event from Jan 24 1:00 PM - Jan 25 1:30 PM
 * isMultiDayEvent({ startTime: '2026-01-24T13:00:00Z', endTime: '2026-01-25T13:30:00Z' }) // true
 *
 * // Event from Jan 24 1:00 PM - Jan 24 5:00 PM
 * isMultiDayEvent({ startTime: '2026-01-24T13:00:00Z', endTime: '2026-01-24T17:00:00Z' }) // false
 */
export function isMultiDayEvent(event: { startTime: string; endTime: string }): boolean {
  const start = parseISO(event.startTime);
  const end = parseISO(event.endTime);
  return !isSameDay(start, end);
}

/**
 * Get the portion of a multi-day event that should display on a specific date.
 *
 * Returns null if the event doesn't overlap with the target date.
 *
 * For events that span multiple days:
 * - isStartDay: Event starts on this date (show from start time to end of day)
 * - isEndDay: Event ends on this date (show from start of day to end time)
 * - isMiddleDay: Event spans this entire day (show as all-day)
 *
 * @example
 * // Event from Jan 24 1:00 PM - Jan 26 3:00 PM
 * getEventDayPortion(event, new Date('2026-01-24')) // { isStartDay: true, isEndDay: false, isMiddleDay: false }
 * getEventDayPortion(event, new Date('2026-01-25')) // { isStartDay: false, isEndDay: false, isMiddleDay: true }
 * getEventDayPortion(event, new Date('2026-01-26')) // { isStartDay: false, isEndDay: true, isMiddleDay: false }
 */
export function getEventDayPortion(
  event: { startTime: string; endTime: string },
  targetDate: Date
): { isStartDay: boolean; isEndDay: boolean; isMiddleDay: boolean } | null {
  const eventStart = parseISO(event.startTime);
  const eventEnd = parseISO(event.endTime);
  const dayStart = startOfDay(targetDate);
  const dayEnd = endOfDay(targetDate);

  // Check if event overlaps with this day
  if (eventEnd <= dayStart || eventStart >= dayEnd) {
    return null;
  }

  const isStartDay = isSameDay(eventStart, targetDate);
  const isEndDay = isSameDay(eventEnd, targetDate);
  const isMiddleDay = !isStartDay && !isEndDay;

  return { isStartDay, isEndDay, isMiddleDay };
}

/**
 * Format date range for multi-day events for display.
 *
 * @example
 * // All-day multi-day event
 * formatMultiDayRange({ allDay: true, startTime: '2026-01-24T00:00:00Z', endTime: '2026-01-26T00:00:00Z' })
 * // Returns: "Jan 24 - 26"
 *
 * // Timed multi-day event
 * formatMultiDayRange({ allDay: false, startTime: '2026-01-24T13:00:00Z', endTime: '2026-01-25T13:30:00Z' })
 * // Returns: "Jan 24 1:00 PM - Jan 25 1:30 PM"
 */
export function formatMultiDayRange(event: {
  startTime: string;
  endTime: string;
  allDay?: boolean;
}): string {
  const start = parseISO(event.startTime);
  const end = parseISO(event.endTime);

  if (event.allDay) {
    // For all-day events, show date range without times
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
  }

  // For timed events, show full datetime range
  return `${format(start, 'MMM d h:mm a')} - ${format(end, 'MMM d h:mm a')}`;
}
